import { isMobile } from 'react-device-detect';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import Checkbox from 'components/checkbox';
import { HeaderLoading, Spinner } from 'components/loadings';
import { ActionModal } from 'components/modal';
import Notification from 'components/notification';
import SearchEmptyState from 'components/searchEmptyState';
import { Pagination, Row, SortableHeader, Top } from 'components/Table';
import { ACC_OWNER, END_USER } from 'constants/roles';
import { SessionContext } from 'contexts/session';
import { UsersContext } from 'contexts/users';
import {
  assignAddonLicense,
  resendUserInvites,
  setPendingInvitesAddon,
  setTeamManager,
  setUserLicense,
  setUserRole,
  setUserTeams,
  unassignAddonLicense,
} from './actions';
import { UserTeamsPayload } from './types';
import { IGenericUsersTableProps, ITableColumn, getUsersTableConfig } from './helper';
import StaffUserRow from './StaffUserRow';
import {
  HeaderScrollContainer,
  StickyHeaderWrapper,
  Subcolumn,
  TableInnerContent,
  TableScrollWrapper,
  UserTableContainer,
} from './styles';
import UserRow, { ContainerDesktop } from './UserRow';
import { SetInvitesAddonSettings, SwitchActions, SwitchLicenseActions } from './types';
import Tooltip from 'components/tooltip';
import Icon from 'components/icons';
import { fromOrdering, toOrdering, getNextSortState, getColumnDirection } from 'utils/sorting';
import SortTable from 'components/sortTable';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FEATURE_FLAGS, useBooleanFlag } from 'hooks/useFeatureFlags';
import useLicenseRejection from 'hooks/useLicenseRejection';
import { LICENSE_UPDATED, LICENSE_UPDATE_FAILED } from 'constants/licenses';
import { findAssignedSkillDive } from 'hooks/useSkillDiveToggleState';
import { NOTIFICATION_TYPES } from 'constants/components/notification';
import { skillDiveLicenseChangeFailed, skillDiveLicenseChanged } from 'constants/skill_dive';

const GenericUsersTable = ({ accountPage, refreshData }: IGenericUsersTableProps): JSX.Element => {
  const { dashboardUsersSorting } = useFlags() || {};
  const { table, actions, selectedRows, loading, fetching, filters } = useContext(UsersContext);
  const {
    teams: accountTeams,
    updateLicensesAvailables,
    isStaff,
    actions: sessionActions,
  } = useContext(SessionContext);
  const skillDiveFlow = useBooleanFlag(FEATURE_FLAGS.SKILL_DIVE_LICENSE_FLOW);
  const notifyLicenseRejection = useLicenseRejection();

  useEffect(() => {
    if (!dashboardUsersSorting && filters.ordering) {
      actions.onChangeFilters({ name: 'ordering', value: '' }, false, true);
    }
  }, [dashboardUsersSorting]);

  const usersTableConfig = getUsersTableConfig(
    isStaff,
    accountPage,
    filters.active === 'true',
    skillDiveFlow
  );
  const tableColumns = usersTableConfig.columns.filter((column): column is ITableColumn =>
    Boolean(column)
  );
  const sortableColumns = tableColumns.filter(
    (column): column is ITableColumn & { columnKey: string } => Boolean(column.columnKey)
  );

  const sortState = fromOrdering(filters.ordering || null);
  const handleSortToggle = (columnKey: string) => {
    const nextState = getNextSortState(sortState, columnKey);
    const nextOrdering = toOrdering(nextState);
    actions.onChangeFilters({ name: 'ordering', value: nextOrdering || '' }, false, true);
  };
  const handleSortSelect = ({ value }: { value: string }) => {
    actions.onChangeFilters({ name: 'ordering', value }, false, true);
  };

  const headerScrollRef = useRef<HTMLDivElement>(null);
  const bodyScrollRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const rafIdRef = useRef<number | null>(null);
  const shadowStateRef = useRef<{ left: boolean; right: boolean }>({ left: false, right: false });
  const visibleWidthRef = useRef<number>(0);

  const updateShadows = useCallback(() => {
    if (rafIdRef.current !== null) return;
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;
      const body = bodyScrollRef.current;
      const container = tableContainerRef.current;
      if (!body || !container) return;
      const { scrollLeft, scrollWidth, clientWidth } = body;
      if (clientWidth > 0 && visibleWidthRef.current !== clientWidth) {
        visibleWidthRef.current = clientWidth;
        container.style.setProperty('--analytics-visible-width', `${clientWidth}px`);
      }
      const nextLeft = scrollLeft > 0;
      const nextRight = scrollLeft < scrollWidth - clientWidth - 1;
      if (shadowStateRef.current.left !== nextLeft) {
        container.dataset.shadowLeft = nextLeft ? 'true' : '';
        shadowStateRef.current.left = nextLeft;
      }
      if (shadowStateRef.current.right !== nextRight) {
        container.dataset.shadowRight = nextRight ? 'true' : '';
        shadowStateRef.current.right = nextRight;
      }
    });
  }, []);

  const handleBodyScroll = useCallback(() => {
    const body = bodyScrollRef.current;
    const header = headerScrollRef.current;
    // Header mirrors the body; one-way sync avoids the jitter feedback loop
    if (body && header && header.scrollLeft !== body.scrollLeft) {
      header.scrollLeft = body.scrollLeft;
    }
    updateShadows();
  }, [updateShadows]);

  useEffect(() => {
    handleBodyScroll();
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [table?.rows, handleBodyScroll]);

  useEffect(() => {
    window.addEventListener('resize', handleBodyScroll);
    return () => window.removeEventListener('resize', handleBodyScroll);
  }, [handleBodyScroll]);

  const [actionsFetching, setActionsFetching] = useState(false);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [actionModalOptions, setActionModalOptions] = useState({
    title: '',
    description: '',
    buttons: [],
    onCancel: null,
  });

  const countRows = selectedRows?.length || 0;
  const selectedAllRows = Boolean(countRows > 0 && selectedRows?.length === table?.rows?.length);
  const intermediateSelection = Boolean(
    countRows > 0 && selectedRows?.length !== table?.rows?.length
  );

  const handleSelectRow = (id: string) => actions?.onSelectRow(id);
  const handleSelectAllRows = () => actions?.onSelectAllRows();

  const handleSwitchLicense = async ({ ids, checked }: SwitchLicenseActions) => {
    const releasesSkillDive =
      !checked && table?.rows?.some((row) => ids.includes(row.id) && findAssignedSkillDive(row));
    setActionsFetching(true);
    const response = await setUserLicense({ ids, license: checked });
    setActionsFetching(false);
    if (!(response instanceof Error) && response.result) {
      actions.onRefreshData();
      updateLicensesAvailables(checked, true).then(() => {
        Notification({ text: LICENSE_UPDATED, type: NOTIFICATION_TYPES.SUCCESS });
      });
      actions.onRefreshData();
      if (releasesSkillDive) {
        sessionActions.refreshSession();
        sessionActions.refreshAccounts();
      }
    } else {
      notifyLicenseRejection(response, {
        fallback: LICENSE_UPDATE_FAILED,
        onRefresh: actions.onRefreshData,
      });
    }
  };

  const handleSwitchAddon = async (
    assigned_to?: number | string,
    addon?: string,
    license_addon_id?: string
  ) => {
    setActionsFetching(true);
    const is_assigning = !license_addon_id;
    let response = null;
    if (is_assigning) {
      response = await assignAddonLicense({ assigned_to, addon });
    } else {
      response = await unassignAddonLicense({ license_addon_id });
    }
    setActionsFetching(false);
    if (!(response instanceof Error) && response) {
      actions.onRefreshData();
      Notification({
        text: skillDiveLicenseChanged(is_assigning),
        type: NOTIFICATION_TYPES.SUCCESS,
      });
    } else {
      notifyLicenseRejection(response, {
        fallback: skillDiveLicenseChangeFailed(is_assigning),
        onRefresh: actions.onRefreshData,
      });
    }
  };

  const handleSwitchInviteAddon = async ({ ids, assign, addon }: SetInvitesAddonSettings) => {
    setActionsFetching(true);
    const response = await setPendingInvitesAddon({ ids, assign, addon });
    setActionsFetching(false);
    if (!(response instanceof Error) && response?.result) {
      actions.onRefreshData();
      sessionActions.refreshSession();
      sessionActions.refreshAccounts();
      Notification({ text: skillDiveLicenseChanged(assign), type: NOTIFICATION_TYPES.SUCCESS });
    } else {
      notifyLicenseRejection(response, {
        fallback: skillDiveLicenseChangeFailed(assign),
        onRefresh: actions.onRefreshData,
      });
    }
  };

  const handleSelectTeam = async (id: string, newTeams: UserTeamsPayload[]) => {
    setActionsFetching(true);
    const response = await setUserTeams({ user_invite_id: id, teams: newTeams });
    setActionsFetching(false);
    if (!(response instanceof Error) && response.result) {
      actions.onRefreshData();
      Notification({ text: 'Teams updated', type: 'success' });
    } else {
      Notification({ text: 'Teams couldn’t be updated', type: 'error' });
    }
  };

  const handleSwitchManager = async (user_invite_id: string, teamsData: UserTeamsPayload[]) => {
    setActionsFetching(true);
    const response = await setTeamManager({
      user_invite_id: user_invite_id,
      teams: teamsData,
    });
    setActionsFetching(false);
    if (!(response instanceof Error) && response.result) {
      actions.onRefreshData();
      Notification({ text: 'Role updated', type: 'success' });
    } else {
      Notification({ text: 'Role couldn’t be updated', type: 'error' });
    }
  };

  const handleSwitchOwner = async ({ id, checked }: SwitchActions) => {
    setActionsFetching(true);
    const response = await setUserRole({ id, role: checked ? ACC_OWNER : END_USER });
    setActionsFetching(false);
    if (!(response instanceof Error) && response.result) {
      actions.onRefreshData();
      Notification({ text: 'Role updated', type: 'success' });
      accountPage && refreshData();
    } else {
      Notification({ text: 'Role couldn’t be updated', type: 'error' });
    }
  };

  const handleCancelInvitation = (ids: string[], name: string[]) => {
    CancelInviteModal({
      name: name[0],
      onAccept: () => actions?.onRemoveTableItem(ids, 'Invitation successfully cancelled'),
    });
  };

  const CancelInviteModal = ({ name, onAccept }) => {
    setActionModal({
      title: 'Cancel Invite',
      description: `Are you sure you want to cancel ${name} invite?`,
      onCancel: () => {
        setActionModalOpen(false);
      },
      buttons: [
        { label: 'Cancel', type: 'secondary', onClick: () => setActionModalOpen(false) },
        {
          label: 'Remove Invite',
          type: 'primary',
          onClick: () => {
            onAccept();
            setActionModalOpen(false);
          },
        },
      ],
    });
  };

  const handleRemoveActiveUser = (ids: string[], name: string[]) => {
    RemoveUserModal({
      name: name[0],
      onAccept: () => actions?.onRemoveTableItem(ids, 'Successfully removed user'),
    });
  };

  const RemoveUserModal = ({ name, onAccept }) => {
    setActionModal({
      title: 'Remove User',
      description: `Are you sure you want to remove ${name}?`,
      onCancel: () => {
        setActionModalOpen(false);
      },
      buttons: [
        { label: 'Cancel', type: 'secondary', onClick: () => setActionModalOpen(false) },
        {
          label: 'Remove user',
          type: 'primary',
          onClick: () => {
            onAccept();
            setActionModalOpen(false);
          },
        },
      ],
    });
  };

  const handleResendInvitation = async (ids: string[]) => {
    setActionsFetching(true);
    const response = await resendUserInvites({ ids });
    setActionsFetching(false);
    if (!(response instanceof Error) && response.result) {
      Notification({ text: 'Invitation successfully resent', type: 'success' });
    } else {
      Notification({ text: 'Invitation couldn’t be updated', type: 'error' });
    }
  };

  const setActionModal = ({ title, description, buttons, onCancel }) => {
    setActionModalOpen(true);
    setActionModalOptions({
      title: title,
      description: description,
      buttons: buttons,
      onCancel: onCancel,
    });
  };

  if (table?.rows?.length < 1)
    return (
      <>
        {fetching ? (
          <Spinner active={fetching} top={isMobile ? '270px' : '240px'} />
        ) : (
          <SearchEmptyState />
        )}
      </>
    );
  return (
    <>
      <Spinner active={fetching} top={isMobile ? '270px' : '240px'} />
      <UserTableContainer lock={fetching || actionsFetching} ref={tableContainerRef}>
        <StickyHeaderWrapper>
          <HeaderScrollContainer ref={headerScrollRef}>
            <TableInnerContent>
              <Row
                className="users-table-header-row"
                columns={usersTableConfig.rowTableConfig}
                transparent={isMobile}
                topSelect={isMobile}
                top={true}
                style={{ padding: 0 }}
              >
                <Top
                  topSelect={isMobile}
                  style={!isMobile ? { justifyContent: 'flex-start', paddingLeft: 0 } : {}}
                >
                  <Checkbox
                    checked={selectedAllRows}
                    indeterminate={intermediateSelection}
                    id="allUserSelected"
                    onChange={handleSelectAllRows}
                    label={isMobile ? `${countRows} users selected` : undefined}
                    controlled
                    darkBackground={isMobile}
                    style={{
                      opacity: '0.6',
                      marginLeft: isMobile ? '10px' : '',
                    }}
                  />
                  {isMobile && dashboardUsersSorting && (
                    <SortTable
                      title="Sort by"
                      name="ordering"
                      position="left"
                      items={[
                        { label: 'Clear sort', value: '', selected: !filters.ordering },
                        ...sortableColumns.flatMap((column) => [
                          {
                            label: `${column.name} ASC`,
                            value: column.columnKey,
                            selected: filters.ordering === column.columnKey,
                          },
                          {
                            label: `${column.name} DESC`,
                            value: `-${column.columnKey}`,
                            selected: filters.ordering === `-${column.columnKey}`,
                          },
                        ]),
                      ]}
                      onSelect={handleSortSelect}
                    />
                  )}
                </Top>
                {usersTableConfig.columns.map((column, index) => (
                  <ContainerDesktop key={index}>
                    {column && (
                      <Top hasSubcolumn={column.subcolumns !== null && filters.active === 'true'}>
                        {column.columnKey && dashboardUsersSorting ? (
                          <SortableHeader
                            label={column.name}
                            columnKey={column.columnKey}
                            direction={getColumnDirection(sortState, column.columnKey)}
                            onToggle={handleSortToggle}
                            tooltip={column.tooltip}
                          />
                        ) : (
                          <span>{column.name}</span>
                        )}

                        {filters.active === 'true' && column.subcolumns && (
                          <Subcolumn>
                            {column.subcolumns?.map((subcolumn, index) => (
                              <span key={index}>{subcolumn}</span>
                            ))}
                          </Subcolumn>
                        )}
                      </Top>
                    )}
                  </ContainerDesktop>
                ))}
                <ContainerDesktop />
              </Row>
            </TableInnerContent>
          </HeaderScrollContainer>
        </StickyHeaderWrapper>
        <TableScrollWrapper ref={bodyScrollRef} onScroll={handleBodyScroll}>
          <TableInnerContent>
            {table?.rows?.map((row) =>
              !isStaff ? (
                <UserRow
                  key={row.id}
                  onSelectTeam={handleSelectTeam}
                  onSwitchManager={handleSwitchManager}
                  onSwitchLicense={handleSwitchLicense}
                  onSelectRow={handleSelectRow}
                  onCancelInvitation={handleCancelInvitation}
                  onResendInvitation={handleResendInvitation}
                  onRemoveUser={handleRemoveActiveUser}
                  onSwitchAddon={handleSwitchAddon}
                  onSwitchInviteAddon={skillDiveFlow ? handleSwitchInviteAddon : undefined}
                  rowSelected={selectedRows?.includes(row.id)}
                  active={filters.active === 'true'}
                  accountTeams={accountTeams}
                  row={row}
                  tableGridConfig={usersTableConfig.rowTableConfig}
                  {...row}
                />
              ) : (
                <StaffUserRow
                  key={row.id}
                  onSwitchManager={handleSwitchManager}
                  onSwitchOwner={handleSwitchOwner}
                  onSwitchLicense={handleSwitchLicense}
                  onSelectRow={handleSelectRow}
                  onRemoveUser={handleRemoveActiveUser}
                  onCancelInvitation={handleCancelInvitation}
                  onResendInvitation={handleResendInvitation}
                  onSwitchAddon={handleSwitchAddon}
                  rowSelected={Boolean(selectedRows?.includes(row.id))}
                  active={filters.active === 'true'}
                  row={row}
                  tableGridConfig={usersTableConfig.rowTableConfig}
                  accountPage={accountPage}
                  {...row}
                />
              )
            )}
          </TableInnerContent>
        </TableScrollWrapper>
        {table.rows && (
          <Pagination
            count={table.count}
            page={parseInt(filters.page)}
            page_size={parseInt(filters.page_size)}
            onPagesClick={(value, name) =>
              actions && actions.onChangeFilters({ name: name, value }, false, true)
            }
          />
        )}
        <ActionModal
          title={actionModalOptions.title}
          description={actionModalOptions.description}
          active={actionModalOpen}
          buttons={actionModalOptions.buttons}
          handleCancel={actionModalOptions.onCancel}
        />
        <HeaderLoading portals={true} active={loading} />
      </UserTableContainer>
    </>
  );
};

export default GenericUsersTable;
